// Locations (static demo pages/Location.jsx): one navigation state (the node
// in the URL), two views (decision H0): path bar (C) and tree (B). Drill-down
// tiles per child; a store opens the Store page.
import { useState } from "preact/hooks";
import { can } from "@gimasi/giothub-shell";
import { useT } from "../i18n";
import { AddStoreDialog } from "../components/AddStoreDialog";
import { useFleet } from "../data/fleet";
import { find, rollup, visibleChildren, type LocNode } from "../data/locations";
import { PathBar, STATE_COLOR, TreePanel } from "../components/Nav.jsx";
import { go } from "../lib/nav";
import { navMode, wide } from "../lib/navmode";
import { State } from "./common";
import Store from "./Store";

export default function Location({ params }: { params: Record<string, string | undefined> }) {
  const t = useT();
  const f = useFleet(t.lang);
  if (!f) return <div class="page"><State loading /></div>;
  const id = decodeURIComponent(params["*"] || params.wild || "") || f.root.id;
  const root = f.root;
  const node = find(root, id) || root;
  const site = node.level === "site" ? node : node.level === "floor" ? node.parent : node.level === "zone" ? node.parent?.parent : null;
  const tree = (navMode.value.monitor || "path") === "tree" && wide();
  const body = site ? <Store key={site.id} site={site} /> : <Drill node={node} />;
  return (
    <>
      <PathBar node={node} scope={null} />
      {tree ? <div class="with-tree"><TreePanel root={root} node={node} scope={null} onSelect={undefined} /><div style="min-width:0">{body}</div></div> : body}
    </>
  );
}

function Drill({ node }: { node: LocNode }) {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const up = (lv: string) => { for (let n: LocNode | null = node; n; n = n.parent) if (n.level === lv) return n.name; return ""; };
  const kids = visibleChildren(node);
  const r = rollup(node);
  return (
    <div class="page">
      <div class="page-h">
        <div><h1>{node.name}</h1><div class="sub"><span class="lbl">{t("lvl." + node.level)}</span>· {t("loc.summary", { s: t.num(r.sites), c: t.num(r.cabinets) })}{r.crit > 0 && <span class="st st-crit">{t("st.nCrit", { n: r.crit })}</span>}{r.alarm > 0 && <span class="st st-alarm">{t("st.nAlarm", { n: r.alarm })}</span>}{r.warn > 0 && <span class="st st-warn">{t("st.nWarn", { n: r.warn })}</span>}{r.delayed > 0 && <span class="st st-delayed">{t("st.nDelayed", { n: r.delayed })}</span>}</div></div>
        {can("labels:manage") && <div class="row"><button class="btn pri" type="button" onClick={() => setAdding(true)}>{t("x.store.add")}</button></div>}
      </div>
      {adding && <AddStoreDialog city={up("city")} area={up("area")} onClose={() => setAdding(false)} />}
      {kids.length === 0 && <p style="color:var(--muted)">{t("x.noLocations2")}</p>}
      <div class="tiles">
        {kids.map((c, i) => { const rr = rollup(c); const tot = rr.sites || 1; return (
          <button key={c.id} type="button" class="tile" style={{ animation: `rise .4s ${i * 0.03}s both` }} onClick={() => go("/loc/" + c.id)}>
            <div class="row" style="justify-content:space-between"><span class="lbl">{t("lvl." + c.level)}</span>{rr.worst !== "ok" && <span class={"st st-" + rr.worst}>{t("st." + rr.worst)}</span>}</div>
            <h3>{c.name}</h3>
            <div class="bar" aria-hidden="true">{(["crit", "alarm", "warn", "delayed", "ok"] as const).map((k) => rr[k] ? <i key={k} style={{ width: `${Math.max((rr[k] / tot) * 100, 4)}%`, background: STATE_COLOR[k] }} /> : null)}</div>
            <span class="mono" style="color:var(--muted);font-size:13px">{c.level === "site" ? t("loc.cabs", { n: c.points.length }) : t("loc.summary", { s: t.num(rr.sites), c: t.num(rr.cabinets) })}</span>
          </button>); })}
      </div>
      <style>{"@keyframes rise{from{opacity:0;transform:translateY(8px)}}"}</style>
    </div>
  );
}
